/**
 * @fileoverview Pure date, slot, and appointment helpers for CustomCalendar.
 * @module components/CustomCalendar/customCalendarUtils
 */
import {
  hasBusinessEventOnDate,
  isTimeSlotInPast,
  isTimeSlotOverlappingBreak,
} from "../../utils/calendar/calendarTimeUtils.js";
import {
  getAppointmentsForDateTime,
  hasCompanyUserConflict,
} from "../../utils/calendar/calendarAppointmentUtils.js";

/**
 * Five weekdays starting on the Monday of the anchor date's week.
 * @param {Date} anchorDate
 * @returns {{ monday: Date, dates: Date[] }}
 */
export function buildWeekdayDates(anchorDate) {
  const dayOfWeek = anchorDate.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday = 1, Sunday = 0
  const monday = new Date(anchorDate);
  monday.setDate(anchorDate.getDate() - daysToSubtract);

  const dates = [];
  for (let day = 0; day < 5; day++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + day);
    dates.push(date);
  }

  return { monday, dates };
}

export function getMonthName(date, language) {
  const monthName = date.toLocaleDateString(language, { month: "long" });
  return monthName.charAt(0).toUpperCase() + monthName.slice(1);
}

export function eventSpansMultipleMonths(eventData) {
  if (
    !eventData ||
    !eventData.fechaInicioEvento ||
    !eventData.fechaFinEvento
  ) {
    return false;
  }

  const eventStartDate = new Date(eventData.fechaInicioEvento + "T00:00:00");
  const eventEndDate = new Date(eventData.fechaFinEvento + "T00:00:00");

  return (
    eventStartDate.getMonth() !== eventEndDate.getMonth() ||
    eventStartDate.getFullYear() !== eventEndDate.getFullYear()
  );
}

export function getDynamicMonthDisplay(eventData, currentDate, language) {
  if (
    !eventData ||
    !eventData.fechaInicioEvento ||
    !eventData.fechaFinEvento
  ) {
    return `${getMonthName(currentDate, language)} ${currentDate.getFullYear()}`;
  }

  const eventStartDate = new Date(eventData.fechaInicioEvento + "T00:00:00");
  const eventEndDate = new Date(eventData.fechaFinEvento + "T00:00:00");

  if (eventSpansMultipleMonths(eventData)) {
    const startMonth = getMonthName(eventStartDate, language);
    const endMonth = getMonthName(eventEndDate, language);
    const year = eventStartDate.getFullYear();

    if (eventStartDate.getFullYear() === eventEndDate.getFullYear()) {
      return `${startMonth} - ${endMonth} ${year}`;
    } else {
      return `${startMonth} ${eventStartDate.getFullYear()} - ${endMonth} ${eventEndDate.getFullYear()}`;
    }
  }

  return `${getMonthName(eventStartDate, language)} ${eventStartDate.getFullYear()}`;
}

export function isSenderAppointment(appointment, senderAppointments) {
  return senderAppointments.some(
    (senderAppt) =>
      senderAppt._id === appointment._id ||
      (senderAppt.fecha === appointment.fecha &&
        senderAppt.horaInicio === appointment.horaInicio &&
        senderAppt.horaFin === appointment.horaFin),
  );
}

export function isCurrentAppointment(appointment, currentAppointment) {
  return currentAppointment && currentAppointment._id === appointment._id;
}

export function isAppointmentDraggable(
  appointment,
  { disableDragAndDrop, allowDragAllAppointments, currentAppointment },
) {
  if (disableDragAndDrop) {
    return false;
  }

  if (allowDragAllAppointments) {
    return true;
  }

  return isCurrentAppointment(appointment, currentAppointment);
}

export function isToday(date) {
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

export function isCurrentTime(timeSlot) {
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  if (currentHour === timeSlot.hour) {
    const slotStartMinute = timeSlot.minute;
    const slotEndMinute = timeSlot.minute + timeSlot.durationMinutes;

    if (currentMinute >= slotStartMinute && currentMinute < slotEndMinute) {
      const positionInSlot =
        ((currentMinute - slotStartMinute) / timeSlot.durationMinutes) * 100;
      return positionInSlot;
    }
  }
  return false;
}

export function getBreakInfo(breakTimes) {
  if (!breakTimes?.horaInicioBreak || !breakTimes?.horaFinBreak) {
    return null;
  }

  return {
    startTime: breakTimes.horaInicioBreak,
    endTime: breakTimes.horaFinBreak,
  };
}

export function isValidDropTarget({
  date,
  timeSlot,
  breakTimes,
  eventData,
  appointments,
}) {
  if (!timeSlot.isValid) {
    return false;
  }

  if (isTimeSlotOverlappingBreak(timeSlot, breakTimes)) {
    return false;
  }

  if (!hasBusinessEventOnDate(date, eventData)) {
    return false;
  }

  if (isTimeSlotInPast(date, timeSlot)) {
    return false;
  }

  const existingAppointments = getAppointmentsForDateTime(
    appointments,
    date,
    timeSlot,
  );
  if (existingAppointments.length > 0) {
    return false;
  }

  return true;
}

export function isValidTimeSlot({
  date,
  timeSlot,
  breakTimes,
  eventData,
  appointments,
  allAppointments,
  draggedAppointment,
  currentAppointment,
}) {
  if (!timeSlot.isValid) {
    return false;
  }

  if (isTimeSlotOverlappingBreak(timeSlot, breakTimes)) {
    return false;
  }

  if (!hasBusinessEventOnDate(date, eventData)) {
    return false;
  }

  if (isTimeSlotInPast(date, timeSlot)) {
    return false;
  }

  const existingAppointments = getAppointmentsForDateTime(
    appointments,
    date,
    timeSlot,
  );
  if (existingAppointments.length > 0) {
    return false;
  }

  if (
    hasCompanyUserConflict({
      date,
      timeSlot,
      appointmentToCheck: draggedAppointment || currentAppointment,
      allAppointments,
      appointments,
    })
  ) {
    return false;
  }

  return true;
}

export function isFirstBreakSlot(isInBreak, slotIndex, timeSlots, breakTimes) {
  if (!isInBreak) {
    return false;
  }
  if (slotIndex === 0) {
    return true;
  }
  const prevSlot = timeSlots[slotIndex - 1];
  return prevSlot && !isTimeSlotOverlappingBreak(prevSlot, breakTimes);
}

export function isSelectedTimeSlot(
  selectedDate,
  selectedTime,
  selectedMinute,
  date,
  slot,
) {
  return (
    selectedDate &&
    selectedTime !== null &&
    selectedDate.toDateString() === date.toDateString() &&
    selectedTime === slot.hour &&
    selectedMinute === slot.minute
  );
}

export function isDragOverTimeSlot(dragOverTimeSlot, date, slot) {
  return (
    dragOverTimeSlot?.date.toDateString() === date.toDateString() &&
    dragOverTimeSlot?.timeSlot?.hour === slot.hour &&
    dragOverTimeSlot?.timeSlot?.minute === slot.minute
  );
}

export function getDaySlotClassName(
  styles,
  {
    isBusinessEventDay,
    isCurrentHour,
    isDragOver,
    isValidDrop,
    isSelected,
    isPast,
    draggedAppointment,
    hasConflict,
    currentAppointment,
    isValidSlot,
    slot,
    isInBreak,
  },
) {
  return `${styles.timeSlot} ${
    isBusinessEventDay ? styles.businessEventDay : styles.nonBusinessEventDay
  } ${isCurrentHour ? styles.currentTime : ""} ${
    isDragOver && isValidDrop ? styles.dragOver : ""
  } ${isSelected ? styles.selected : ""} ${
    isPast ? styles.pastTimeSlot : ""
  } ${draggedAppointment && !isValidDrop ? styles.invalidDropTarget : ""} ${
    (draggedAppointment || currentAppointment) && hasConflict
      ? styles.companyUserConflict
      : ""
  } ${!isValidSlot ? styles.invalidTimeSlot : ""} ${
    !slot.isValid ? styles.invalidDurationSlot : ""
  } ${isInBreak ? styles.breakSlot : ""}`;
}

export function formatAppointmentEventTime(appointment) {
  if (
    appointment.teamsJoinUrl &&
    appointment.horaInicioCitaVirtual &&
    appointment.diaCitaVirtual &&
    appointment.estadoCitaVirtual === "approved"
  ) {
    if (appointment.horaInicioCitaVirtual.includes("T")) {
      const virtualDateTime = new Date(appointment.horaInicioCitaVirtual);
      return `${virtualDateTime.getUTCHours().toString().padStart(2, "0")}:${virtualDateTime.getUTCMinutes().toString().padStart(2, "0")}`;
    }
    return appointment.horaInicioCitaVirtual;
  }
  return `${appointment.horaInicio} - ${appointment.horaFin}`;
}
